import { ModuleId, RbacRole, RbacTier, UserType } from '../../types';

/**
 * RBAC v2 predicates. Ports mk-admin-app's `src/common/utils/rbac.ts` so the
 * gating logic in mk-addax stays in lockstep with the new admin.
 *
 * `user.rbac` is present iff the user is on a v2 tenant AND has a
 * `user_assignments[]` entry (per RGI-1042). Absence is the v1 signal —
 * legacy users see zero behavioral change at every layer (RGI-1024).
 *
 * SuperKudu bypass is enforced at the call site, not here, so the predicate
 * surface stays small and explicit. Callers should typically do:
 *   user?.superKudu || hasModule(user, moduleId)
 */

const TIER_ORDER: Record<RbacTier, number> = {
  starter: 0,
  growth: 1,
  enterprise: 2,
};

export function isV2User(user: UserType | null | undefined): boolean {
  return !!user?.rbac;
}

export function hasModule(
  user: UserType | null | undefined,
  moduleId: ModuleId
): boolean {
  return !!user?.rbac?.assignedModules.includes(moduleId);
}

export function tenantPurchased(
  user: UserType | null | undefined,
  moduleId: ModuleId
): boolean {
  return !!user?.rbac?.purchasedModules.includes(moduleId);
}

export function hasRole(
  user: UserType | null | undefined,
  requiredRole: RbacRole
): boolean {
  return user?.rbac?.role === requiredRole;
}

export function hasTier(
  user: UserType | null | undefined,
  minTier: 'growth' | 'enterprise'
): boolean {
  if (!user?.rbac) return false;
  return TIER_ORDER[user.rbac.tier] >= TIER_ORDER[minTier];
}

/**
 * Declarative access requirement for a sidebar item or app area.
 * An empty requirement (or `undefined`) means baseline — visible to everyone.
 */
export interface AccessRequirement {
  /** Only users with this v2 role pass (e.g. admin-only areas). */
  requiresRole?: RbacRole;
  /** Pass if the user is assigned ANY of these modules. */
  requiresModuleAny?: ModuleId[];
  /** Pass if the ORG purchased any of these modules — for areas that exist
   * for a module regardless of who is assigned to it (RGI-2609). */
  requiresPurchasedAny?: ModuleId[];
}

/** Data Studio's admin-side pages (conversion and audience mapping): only
 * orgs that bought Data Studio see them (RGI-2693). Org purchase, not user
 * assignment: an org admin without the module still administers it. Mirrors
 * mk-admin-app's constant of the same name. */
export const DATA_STUDIO_TENANT: AccessRequirement = {
  requiresPurchasedAny: ['data_studio'],
};

/**
 * Single predicate gating sidebar items / areas by role and/or module.
 * Ports mk-admin-app's `canSee` so both apps gate the v2 sidebar identically.
 *
 * - Baseline (no requirement): everyone sees it.
 * - SuperKudu and v1 users (no rbac claim) bypass all checks (full access).
 * - v2 users must satisfy every declared dimension (role AND module).
 */
export function canSee(
  user: UserType | null | undefined,
  req?: AccessRequirement
): boolean {
  if (
    !req ||
    (!req.requiresRole && !req.requiresModuleAny && !req.requiresPurchasedAny)
  ) {
    return true;
  }
  if (!user) return false;
  if (user.superKudu) return true;
  if (!isV2User(user)) return true;
  if (req.requiresRole && !hasRole(user, req.requiresRole)) return false;
  if (
    req.requiresModuleAny &&
    !req.requiresModuleAny.some((moduleId) => hasModule(user, moduleId))
  ) {
    return false;
  }
  if (
    req.requiresPurchasedAny &&
    !req.requiresPurchasedAny.some((moduleId) =>
      tenantPurchased(user, moduleId)
    )
  ) {
    return false;
  }
  return true;
}

/**
 * The legacy role (`admin` / `user`) addax gates on through `user.role`.
 * On a v2 tenant `rbac.role` is the source of truth: Users & Teams changes
 * only the RBAC assignment and never the legacy `permissions[].role`, so a
 * promoted org admin still reads `user` there (RGI-3008). v1 users keep the
 * legacy permission for the tenant.
 */
export function legacyRole(
  user: UserType | null | undefined,
  tenantId: number
): string {
  if (isV2User(user)) return hasRole(user, 'orgAdmin') ? 'admin' : 'user';
  const permission = user?.permissions?.find((p) => p.tenant === tenantId);
  return permission?.role || 'user';
}
